import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Connectors Marketplace — the discover/attach surface for integration capabilities, folding
 * the two sides together into one browse-able gallery:
 *
 *  1. Built-in connector packages (services/chat/tools/connectors.js) — compile-time groups
 *     of the platform's tools (web, github, crm, …) that agents enable per-capability. Read-only
 *     here; the toggle lives on each agent's Capabilities tab.
 *
 *  2. Integration providers (services/connections/providers.js) — third-party services
 *     (Slack, GitHub, SMTP, object storage, …) the company connects credentials to. "Connect"
 *     links to the Connections screen, where the credential/OAuth capture happens.
 *
 * This is a catalogue, not an admin CRUD screen — the install/attach gesture for the workflow
 * builder happens at the workflow's palette; the credential gesture happens in Connections.
 */

function ConnectorCard({ c }) {
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{c.label}</span>
        <Badge variant="secondary" size="sm">built-in</Badge>
      </div>
      <p className="text-xs text-secondary-500">{c.description}</p>
      <div className="flex flex-wrap gap-1">
        {(c.tools || []).map((name) => <Badge key={name} variant="outline" size="sm">{name}</Badge>)}
      </div>
      <p className="text-[11px] text-secondary-400">
        {(c.tools || []).length} tools — attach from the workflow builder&apos;s palette; enable/disable per agent on its Capabilities tab.
      </p>
    </Card>
  );
}

function ProviderCard({ p }) {
  return (
    <Card className="p-4 space-y-2 flex flex-col justify-between">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{p.label}</span>
          {p.is_active ? null : <Badge variant="secondary" size="sm">inactive</Badge>}
        </div>
        <p className="text-xs text-secondary-500">{p.description}</p>
      </div>
      <Link to="/app-connections" className="text-xs font-medium text-primary-600 hover:underline">Connect credentials →</Link>
    </Card>
  );
}

export default function ConnectorsPage() {
  const { fetchApi } = useAppApi();
  const [connectors, setConnectors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    setErr('');
    return Promise.all([
      fetchApi('agentConnectors').then((res) => res?.connectors || []).catch(() => []),
      fetchApi('connectionProviders').then((res) => res || {}).catch(() => ({})),
    ])
      .then(([conns, spec]) => {
        setConnectors(conns);
        setCategories(spec.categories || []);
        setProviders(spec.providers || {});
      })
      .catch((ex) => setErr(ex?.response?.data?.error || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return <Loader text="Loading marketplace…" />;
  if (err) return <Alert variant="error">{err}</Alert>;

  return (
    <PageShell className="space-y-5">
      <PageHeader
        title="Connectors Marketplace"
        subtitle="Browse the integration capabilities your agents and workflows can use — built-in tool packages, and third-party services you attach credentials to."
      />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Built-in connector packages</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {connectors.map((c) => <ConnectorCard key={c.key} c={c} />)}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Integration providers</h2>
        {categories.map((cat) => {
          const catProviders = (cat.providers || [])
            .map((key) => providers[key])
            .filter(Boolean);
          if (!catProviders.length) return null;
          return (
            <div key={cat.key} className="space-y-2">
              <h3 className="text-sm font-medium text-secondary-500">{cat.name}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {catProviders.map((p) => <ProviderCard key={p.key} p={p} />)}
              </div>
            </div>
          );
        })}
      </section>
    </PageShell>
  );
}
